import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    FileText,
    Link,
    Copy,
    ChevronDown,
    Building,
    Loader2,
    BookOpen,
    History,
    Search,
    CheckCircle2,
    Monitor,
    Smartphone,
    AlertTriangle
} from 'lucide-react';
import './UploadPage.css';
import { useApiMutation } from '../hooks/useApi';
import { uploadApi, guidelinesApi, type DeviceMode, type SearchTimeRangeMode } from '../services/api';
import type { Guideline, GuidelineVersionInfo, GuidelineResponse } from '../types/guideline';
import { useToast } from '../components/toast/ToastProvider';

const SEARCH_DATE_RESTRICT_OPTIONS: Array<{ label: string; value: string }> = [
    { label: '過去24時間', value: 'd1' },
    { label: '過去7日間', value: 'w1' },
    { label: '過去30日間', value: 'm1' },
    { label: '過去90日間', value: 'm3' },
    { label: '過去1年間', value: 'y1' },
];

const UploadPage: React.FC = () => {
    const toast = useToast();
    // API hooks
    const { mutate: uploadUrls, loading: urlUploadLoading, error: urlUploadError } = useApiMutation(
        (params: {
            urls: string[];
            guidelineId: string;
            version: number;
            asp: string;
            operationCompany: string;
            media: string;
            jobName: string;
            enableSearch?: boolean;
            deviceMode?: DeviceMode;
            searchTimeRangeMode?: SearchTimeRangeMode;
            searchDateRestrict?: string;
            searchDateFrom?: string;
            searchDateTo?: string;
        }) =>
            uploadApi.uploadUrls(
                params.urls,
                params.guidelineId,
                params.version,
                params.asp,
                params.operationCompany,
                params.media,
                params.jobName,
                params.enableSearch,
                params.deviceMode ?? 'personal_computer',
                params.searchTimeRangeMode,
                params.searchDateRestrict,
                params.searchDateFrom,
                params.searchDateTo
            )
    );
    // Local state for UI
    const [urlList, setUrlList] = useState<string>('');
    const [selectedGuideline, setSelectedGuideline] = useState<string>('');
    const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
    const [selectedASP, setSelectedASP] = useState<string>('');
    const [selectedOperationCompany, setSelectedOperationCompany] = useState<string>('');
    const [selectedMedia, setSelectedMedia] = useState<string>('');
    const [jobName, setJobName] = useState<string>('');
    const [showGuidelineDropdown, setShowGuidelineDropdown] = useState(false);
    const [showVersionDropdown, setShowVersionDropdown] = useState(false);
    const [showASPDropdown, setShowASPDropdown] = useState(false);
    const [showOperationCompanyDropdown, setShowOperationCompanyDropdown] = useState(false);
    const [showMediaDropdown, setShowMediaDropdown] = useState(false);
    const [aspSearchTerm, setAspSearchTerm] = useState('');
    const [operationCompanySearchTerm, setOperationCompanySearchTerm] = useState('');
    const [mediaSearchTerm, setMediaSearchTerm] = useState('');
    const [guidelineSearchTerm, setGuidelineSearchTerm] = useState('');
    const [versionSearchTerm, setVersionSearchTerm] = useState('');
    const [guidelines, setGuidelines] = useState<Omit<Guideline, 'versions'>[]>([]);
    const [versions, setVersions] = useState<GuidelineVersionInfo[]>([]);
    const [loadingGuidelines, setLoadingGuidelines] = useState(false);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const guidelineDropdownRef = useRef<HTMLDivElement>(null);
    const versionDropdownRef = useRef<HTMLDivElement>(null);
    const aspDropdownRef = useRef<HTMLDivElement>(null);
    const operationCompanyDropdownRef = useRef<HTMLDivElement>(null);
    const mediaDropdownRef = useRef<HTMLDivElement>(null);
    const deviceModeDropdownRef = useRef<HTMLDivElement>(null);
    const [showDeviceModeDropdown, setShowDeviceModeDropdown] = useState(false);
    const [versionAttributes, setVersionAttributes] = useState<{
        aspOptions: string[];
        operationCompanyOptions: string[];
        mediaOptions: string[];
        enableSearch?: boolean;
    } | null>(null);
    const [enableSearchMode, setEnableSearchMode] = useState<boolean>(false);
    const [searchTimeRangeMode, setSearchTimeRangeMode] = useState<SearchTimeRangeMode>('none');
    const [searchDateRestrict, setSearchDateRestrict] = useState<string>('w1');
    const [searchDateFrom, setSearchDateFrom] = useState<string>('');
    const [searchDateTo, setSearchDateTo] = useState<string>('');
    const [deviceMode, setDeviceMode] = useState<DeviceMode>('personal_computer');

    // Load guidelines on mount
    useEffect(() => {
        const loadGuidelines = async () => {
            try {
                setLoadingGuidelines(true);
                const data = await guidelinesApi.getGuidelines() as any;
                const guidelinesList = (data?.guidelines || []) as Omit<Guideline, 'versions'>[];
                setGuidelines(guidelinesList);
            } catch (err) {
                console.error('Failed to load guidelines', err);
                setGuidelines([]);
            } finally {
                setLoadingGuidelines(false);
            }
        };
        loadGuidelines();
    }, []);

    // Load versions when guideline is selected
    useEffect(() => {
        const loadVersions = async () => {
            if (!selectedGuideline) {
                setVersions([]);
                setSelectedVersion(null);
                setVersionAttributes(null);
                return;
            }
            try {
                setLoadingVersions(true);
                const data = await guidelinesApi.getGuidelineVersions(selectedGuideline) as any;
                const versionsList = (data?.versions || []) as GuidelineVersionInfo[];
                setVersions(versionsList);
                // Auto-select latest version
                const latest = versionsList.find(v => v.is_latest);
                if (latest) {
                    setSelectedVersion(latest.version);
                } else if (versionsList.length > 0) {
                    setSelectedVersion(versionsList[0].version);
                }
            } catch (err) {
                console.error('Failed to load versions', err);
                setVersions([]);
                setSelectedVersion(null);
            } finally {
                setLoadingVersions(false);
            }
        };
        loadVersions();
    }, [selectedGuideline]);

    // Load version-specific option lists when version changes
    useEffect(() => {
        const loadVersionAttributes = async () => {
            setSelectedASP('');
            setSelectedOperationCompany('');
            setSelectedMedia('');
            setAspSearchTerm('');
            setOperationCompanySearchTerm('');
            setMediaSearchTerm('');
            if (!selectedGuideline || selectedVersion === null) {
                setVersionAttributes(null);
                return;
            }
            try {
                const data = await guidelinesApi.getGuideline(selectedGuideline, selectedVersion) as GuidelineResponse;
                const enableSearch = data?.enableSearch ?? false;
                setVersionAttributes({
                    aspOptions: data?.aspOptions ?? [],
                    operationCompanyOptions: data?.operationCompanyOptions ?? [],
                    mediaOptions: data?.mediaOptions ?? [],
                    enableSearch,
                });
                // Reset search mode when version changes
                setEnableSearchMode(false);
                setSearchTimeRangeMode('none');
                setSearchDateRestrict('w1');
                setSearchDateFrom('');
                setSearchDateTo('');
            } catch (err) {
                console.error('Failed to load version attributes', err);
                setVersionAttributes(null);
                setEnableSearchMode(false);
                setSearchTimeRangeMode('none');
                setSearchDateRestrict('w1');
                setSearchDateFrom('');
                setSearchDateTo('');
            }
        };
        loadVersionAttributes();
    }, [selectedGuideline, selectedVersion]);

    // Filtered lists based on search terms
    const filteredGuidelines = guidelines.filter(guideline =>
        guideline.name.toLowerCase().includes(guidelineSearchTerm.toLowerCase())
    );

    // Filter versions based on search term and sort by creation date (newest first)
    const filteredVersions = useMemo(() => {
        let result = versions;

        // Filter if search term exists
        if (versionSearchTerm.trim()) {
            const searchLower = versionSearchTerm.toLowerCase();
            result = versions.filter(v => {
                const description = v.description || '説明なし';
                const date = new Date(v.created_at).toLocaleDateString('ja-JP');
                const versionText = `v${v.version} ${v.is_latest ? '最新' : ''} ${description} ${date}`;
                return versionText.toLowerCase().includes(searchLower);
            });
        }

        // Sort by creation date (newest first)
        return [...result].sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA; // Descending order (newest first)
        });
    }, [versions, versionSearchTerm]);

    const aspList = [
        'RT',
        'ウェブサポート',
        'キュービック',
        '無限',
        'セレス',
        'CyberOwl',
        'AT',
        'アドトラック',
        'A8',
        'Skyfall'
    ];

    // Filtered lists based on search terms
    const filteredAspList = useMemo(() => {
        const aspList = versionAttributes?.aspOptions ?? [];
        return aspList.filter(asp =>
            asp.toLowerCase().includes(aspSearchTerm.toLowerCase())
        );
    }, [versionAttributes, aspSearchTerm]);

    const filteredOperationCompanyList = useMemo(() => {
        const companies = versionAttributes?.operationCompanyOptions ?? [];
        return companies.filter(company =>
            company.toLowerCase().includes(operationCompanySearchTerm.toLowerCase())
        );
    }, [versionAttributes, operationCompanySearchTerm]);

    const filteredMediaList = useMemo(() => {
        const mediaList = versionAttributes?.mediaOptions ?? [];
        return mediaList.filter(media =>
            media.toLowerCase().includes(mediaSearchTerm.toLowerCase())
        );
    }, [versionAttributes, mediaSearchTerm]);


    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (guidelineDropdownRef.current && !guidelineDropdownRef.current.contains(event.target as Node)) {
                setShowGuidelineDropdown(false);
            }
            if (versionDropdownRef.current && !versionDropdownRef.current.contains(event.target as Node)) {
                setShowVersionDropdown(false);
                setVersionSearchTerm('');
            }
            if (aspDropdownRef.current && !aspDropdownRef.current.contains(event.target as Node)) {
                setShowASPDropdown(false);
            }
            if (operationCompanyDropdownRef.current && !operationCompanyDropdownRef.current.contains(event.target as Node)) {
                setShowOperationCompanyDropdown(false);
            }
            if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
                setShowMediaDropdown(false);
            }
            if (deviceModeDropdownRef.current && !deviceModeDropdownRef.current.contains(event.target as Node)) {
                setShowDeviceModeDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const validateUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const parseUrlList = (text: string): string[] => {
        return text
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
            .filter(url => validateUrl(url));
    };

    const urlValidationSummary = useMemo(() => {
        const lines = urlList
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0);

        const validUrls = lines.filter(line => validateUrl(line));
        const invalidUrls = lines.filter(line => !validateUrl(line));
        const invalidCount = invalidUrls.length;

        return {
            hasAnyInput: urlList.trim().length > 0,
            totalNonEmptyLines: lines.length,
            validCount: validUrls.length,
            invalidCount,
            invalidUrls,
        };
    }, [urlList]);

    const handleUrlSubmit = async () => {
        // Validate required fields
        if (!selectedGuideline) {
            toast.warning('ガイドラインを選択してください', { title: '入力不足' });
            return;
        }
        if (selectedVersion === null) {
            toast.warning('ガイドラインバージョンを選択してください', { title: '入力不足' });
            return;
        }
        if (!selectedMedia) {
            toast.warning('媒体名を選択してください', { title: '入力不足' });
            return;
        }
        if (!selectedOperationCompany) {
            toast.warning('運営会社を選択してください', { title: '入力不足' });
            return;
        }
        if (!selectedOperationCompany) {
            toast.warning('運営会社を選択してください', { title: '入力不足' });
            return;
        }
        if (!jobName.trim()) {
            toast.warning('ジョブ名を入力してください', { title: '入力不足' });
            return;
        }

        const urls = parseUrlList(urlList);
        if (urls.length === 0) {
            toast.warning('有効なURLを入力してください', { title: '入力不足' });
            return;
        }

        if (enableSearchMode && searchTimeRangeMode === 'absolute') {
            if (!searchDateFrom || !searchDateTo) {
                toast.warning('検索期間の開始日と終了日を入力してください', { title: '入力不足' });
                return;
            }
            if (searchDateFrom > searchDateTo) {
                toast.warning('検索期間は開始日が終了日より前になるように設定してください', { title: '入力エラー' });
                return;
            }
        }

        try {
            const searchPayload = enableSearchMode
                ? (searchTimeRangeMode === 'preset'
                    ? {
                        searchTimeRangeMode: 'preset' as SearchTimeRangeMode,
                        searchDateRestrict,
                        searchDateFrom: undefined,
                        searchDateTo: undefined,
                    }
                    : searchTimeRangeMode === 'absolute'
                        ? {
                            searchTimeRangeMode: 'absolute' as SearchTimeRangeMode,
                            searchDateRestrict: undefined,
                            searchDateFrom,
                            searchDateTo,
                        }
                        : {
                            searchTimeRangeMode: 'none' as SearchTimeRangeMode,
                            searchDateRestrict: undefined,
                            searchDateFrom: undefined,
                            searchDateTo: undefined,
                        })
                : {
                    searchTimeRangeMode: undefined,
                    searchDateRestrict: undefined,
                    searchDateFrom: undefined,
                    searchDateTo: undefined,
                };
            const result = await uploadUrls({
                urls,
                jobName: jobName.trim(),
                guidelineId: selectedGuideline,
                version: selectedVersion!,
                asp: selectedASP,
                operationCompany: selectedOperationCompany,
                media: selectedMedia,
                enableSearch: enableSearchMode,
                deviceMode,
                ...searchPayload
            });

            if (result) {
                // Clear form after successful upload
                setUrlList('');
                setSelectedGuideline('');
                setSelectedVersion(null);
                setSelectedASP('');
                setSelectedOperationCompany('');
                setSelectedMedia('');
                setJobName('');
                setEnableSearchMode(false);
                setSearchTimeRangeMode('none');
                setSearchDateRestrict('w1');
                setSearchDateFrom('');
                setSearchDateTo('');

                toast.success('URLが正常にアップロードされました', { title: 'アップロード完了' });
            }
        } catch (error) {
            console.error('URL upload failed:', error);
            toast.error('URLのアップロードに失敗しました', { title: 'アップロード失敗' });
        }
    };



    const handleAspDropdownToggle = () => {
        setShowASPDropdown(!showASPDropdown);
        setShowGuidelineDropdown(false);
        setShowVersionDropdown(false);
        setShowOperationCompanyDropdown(false);
        setShowMediaDropdown(false);
        setShowDeviceModeDropdown(false);
        if (!showASPDropdown) {
            setAspSearchTerm('');
        }
    };

    const handleOperationCompanyDropdownToggle = () => {
        setShowOperationCompanyDropdown(!showOperationCompanyDropdown);
        setShowGuidelineDropdown(false);
        setShowVersionDropdown(false);
        setShowASPDropdown(false);
        setShowMediaDropdown(false);
        setShowDeviceModeDropdown(false);
        if (!showOperationCompanyDropdown) {
            setOperationCompanySearchTerm('');
        }
    };

    const handleMediaDropdownToggle = () => {
        setShowMediaDropdown(!showMediaDropdown);
        setShowGuidelineDropdown(false);
        setShowVersionDropdown(false);
        setShowASPDropdown(false);
        setShowOperationCompanyDropdown(false);
        setShowDeviceModeDropdown(false);
        if (!showMediaDropdown) {
            setMediaSearchTerm('');
        }
    };


    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">アップロード</h1>
                <p className="page-description">
                    アフィリエイトコンテンツ違反の処理対象となるURLリストを入力します。
                </p>
            </div>

            {/* Upload Area */}
            <div className="upload-section">
                {/* URL Input Mode */}
                <div>
                    <div className="url-input-area">
                        <div className="url-input-header">
                            <div className="url-input-icon">
                                <Link size={48} />
                            </div>
                            <h3 className="url-input-title">URLリストを貼り付け</h3>
                            <p className="url-input-description">
                                複数のURLを1行に1つずつ貼り付け
                            </p>
                        </div>
                        <div className="url-input-container">
                            {/* Form Fields */}
                            <div className="form-fields">
                                <div className="form-field form-field-full">
                                    <label className="form-label">
                                        <FileText size={16} />
                                        ジョブ名 *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder="ジョブ名を入力してください"
                                        value={jobName}
                                        onChange={(e) => setJobName(e.target.value)}
                                    />
                                </div>

                                <div className="form-row form-field-full">
                                    <div className="form-field">
                                        <label className="form-label">
                                            <BookOpen size={16} />
                                            ガイドライン *
                                        </label>
                                        <div className="dropdown-container" ref={guidelineDropdownRef}>
                                            <button
                                                className={`dropdown-button ${selectedGuideline ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setShowGuidelineDropdown(!showGuidelineDropdown);
                                                    setShowVersionDropdown(false);
                                                    setShowASPDropdown(false);
                                                    setShowOperationCompanyDropdown(false);
                                                    setShowMediaDropdown(false);
                                                    setShowDeviceModeDropdown(false);
                                                }}
                                                disabled={loadingGuidelines}
                                            >
                                                <span>
                                                    {loadingGuidelines ? '読み込み中...' :
                                                        selectedGuideline ? guidelines.find(g => g.id === selectedGuideline)?.name || selectedGuideline :
                                                            'ガイドラインを選択'}
                                                </span>
                                                <ChevronDown size={16} />
                                            </button>
                                            {showGuidelineDropdown && (
                                                <div className="dropdown-menu">
                                                    <div className="dropdown-search">
                                                        <input
                                                            type="text"
                                                            className="search-input"
                                                            placeholder="ガイドラインを検索..."
                                                            value={guidelineSearchTerm}
                                                            onChange={(e) => setGuidelineSearchTerm(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="dropdown-list">
                                                        {filteredGuidelines.length > 0 ? (
                                                            filteredGuidelines.map((guideline) => (
                                                                <button
                                                                    key={guideline.id}
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        setSelectedGuideline(guideline.id);
                                                                        setShowGuidelineDropdown(false);
                                                                        setGuidelineSearchTerm('');
                                                                    }}
                                                                >
                                                                    {guideline.name}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-no-results">
                                                                {loadingGuidelines ? '読み込み中...' : '検索結果が見つかりません'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        <label className="form-label">
                                            <History size={16} />
                                            ガイドラインバージョン *
                                        </label>
                                        <div className="dropdown-container" ref={versionDropdownRef}>
                                            <button
                                                className={`dropdown-button version-dropdown-button ${selectedVersion !== null ? 'selected' : ''}`}
                                                onClick={() => {
                                                    if (selectedGuideline) {
                                                        setShowVersionDropdown(!showVersionDropdown);
                                                        setShowGuidelineDropdown(false);
                                                        setShowASPDropdown(false);
                                                        setShowOperationCompanyDropdown(false);
                                                        setShowMediaDropdown(false);
                                                        setShowDeviceModeDropdown(false);
                                                        if (!showVersionDropdown) {
                                                            setVersionSearchTerm('');
                                                        }
                                                    }
                                                }}
                                                disabled={!selectedGuideline || loadingVersions}
                                            >
                                                <span>
                                                    {!selectedGuideline ? 'ガイドラインを先に選択' :
                                                        loadingVersions ? '読み込み中...' :
                                                            selectedVersion !== null ? (() => {
                                                                const selectedVersionInfo = versions.find(v => v.version === selectedVersion);
                                                                if (selectedVersionInfo) {
                                                                    return `v${selectedVersionInfo.version}${selectedVersionInfo.is_latest ? ' (最新)' : ''}`;
                                                                }
                                                                return `v${selectedVersion}`;
                                                            })() :
                                                                'バージョンを選択'}
                                                </span>
                                                <ChevronDown size={16} />
                                            </button>
                                            {showVersionDropdown && selectedGuideline && (
                                                <div className="dropdown-menu version-dropdown-menu">
                                                    <div className="dropdown-search">
                                                        <Search size={16} className="search-icon" />
                                                        <input
                                                            type="text"
                                                            className="search-input"
                                                            placeholder="バージョンを検索..."
                                                            value={versionSearchTerm}
                                                            onChange={(e) => setVersionSearchTerm(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="dropdown-list">
                                                        {filteredVersions.length > 0 ? (
                                                            filteredVersions.map((v) => (
                                                                <button
                                                                    key={v.version}
                                                                    className={`dropdown-item ${v.version === selectedVersion ? 'active' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedVersion(v.version);
                                                                        setShowVersionDropdown(false);
                                                                        setVersionSearchTerm('');
                                                                    }}
                                                                >
                                                                    <div className="version-item-content">
                                                                        <div className="version-item-header">
                                                                            <span className="version-number">v{v.version}</span>
                                                                            {v.is_latest && (
                                                                                <span className="version-badge">最新</span>
                                                                            )}
                                                                        </div>
                                                                        <div className="version-item-description">
                                                                            {v.description || '説明なし'}
                                                                        </div>
                                                                        <div className="version-item-date">
                                                                            {new Date(v.created_at).toLocaleDateString('ja-JP')}
                                                                        </div>
                                                                    </div>
                                                                    {v.version === selectedVersion && (
                                                                        <CheckCircle2 size={16} className="version-check-icon" />
                                                                    )}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-no-results">
                                                                {loadingVersions ? '読み込み中...' : '検索結果が見つかりません'}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-row form-row-3-columns form-field-full">
                                    <div className="form-field">
                                        <label className="form-label">
                                            <Building size={16} />
                                            関連ASP
                                        </label>
                                        <div className="dropdown-container" ref={aspDropdownRef}>
                                            <button
                                                className={`dropdown-button ${selectedASP ? 'selected' : ''}`}
                                                onClick={handleAspDropdownToggle}
                                                disabled={!selectedGuideline || selectedVersion === null || !versionAttributes || versionAttributes.aspOptions.length === 0}
                                            >
                                                <span>{selectedASP || 'ASPを選択'}</span>
                                                <ChevronDown size={16} />
                                            </button>
                                            {showASPDropdown && versionAttributes && versionAttributes.aspOptions.length > 0 && (
                                                <div className="dropdown-menu">
                                                    <div className="dropdown-search">
                                                        <input
                                                            type="text"
                                                            className="search-input"
                                                            placeholder="ASPを検索..."
                                                            value={aspSearchTerm}
                                                            onChange={(e) => setAspSearchTerm(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="dropdown-list">
                                                        {filteredAspList.length > 0 ? (
                                                            filteredAspList.map((asp, index) => (
                                                                <button
                                                                    key={`asp-${index}-${asp}`}
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        setSelectedASP(asp);
                                                                        setShowASPDropdown(false);
                                                                        setAspSearchTerm('');
                                                                    }}
                                                                >
                                                                    {asp}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-no-results">
                                                                検索結果が見つかりません
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">
                                            <Building size={16} />
                                            運営会社 *
                                        </label>
                                        <div className="dropdown-container" ref={operationCompanyDropdownRef}>
                                            <button
                                                className={`dropdown-button ${selectedOperationCompany ? 'selected' : ''}`}
                                                onClick={handleOperationCompanyDropdownToggle}
                                                disabled={!selectedGuideline || selectedVersion === null || !versionAttributes || versionAttributes.operationCompanyOptions.length === 0}
                                            >
                                                <span>{selectedOperationCompany || '運営会社を選択'}</span>
                                                <ChevronDown size={16} />
                                            </button>
                                            {showOperationCompanyDropdown && versionAttributes && versionAttributes.operationCompanyOptions.length > 0 && (
                                                <div className="dropdown-menu">
                                                    <div className="dropdown-search">
                                                        <input
                                                            type="text"
                                                            className="search-input"
                                                            placeholder="運営会社を検索..."
                                                            value={operationCompanySearchTerm}
                                                            onChange={(e) => setOperationCompanySearchTerm(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="dropdown-list">
                                                        {filteredOperationCompanyList.length > 0 ? (
                                                            filteredOperationCompanyList.map((company, index) => (
                                                                <button
                                                                    key={`operation-company-${index}-${company}`}
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        setSelectedOperationCompany(company);
                                                                        setShowOperationCompanyDropdown(false);
                                                                        setOperationCompanySearchTerm('');
                                                                    }}
                                                                >
                                                                    {company}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-no-results">
                                                                検索結果が見つかりません
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="form-field">
                                        <label className="form-label">
                                            <Building size={16} />
                                            媒体名 *
                                        </label>
                                        <div className="dropdown-container" ref={mediaDropdownRef}>
                                            <button
                                                className={`dropdown-button ${selectedMedia ? 'selected' : ''}`}
                                                onClick={handleMediaDropdownToggle}
                                                disabled={!selectedGuideline || selectedVersion === null || !versionAttributes || versionAttributes.mediaOptions.length === 0}
                                            >
                                                <span>{selectedMedia || '媒体名を選択'}</span>
                                                <ChevronDown size={16} />
                                            </button>
                                            {showMediaDropdown && versionAttributes && versionAttributes.mediaOptions.length > 0 && (
                                                <div className="dropdown-menu">
                                                    <div className="dropdown-search">
                                                        <input
                                                            type="text"
                                                            className="search-input"
                                                            placeholder="媒体名を検索..."
                                                            value={mediaSearchTerm}
                                                            onChange={(e) => setMediaSearchTerm(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div className="dropdown-list">
                                                        {filteredMediaList.length > 0 ? (
                                                            filteredMediaList.map((media, index) => (
                                                                <button
                                                                    key={`media-${index}-${media}`}
                                                                    className="dropdown-item"
                                                                    onClick={() => {
                                                                        setSelectedMedia(media);
                                                                        setShowMediaDropdown(false);
                                                                        setMediaSearchTerm('');
                                                                    }}
                                                                >
                                                                    {media}
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="dropdown-no-results">
                                                                検索結果が見つかりません
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {versionAttributes?.enableSearch && (
                                    <div className="form-field form-field-full">
                                        <label className="form-label">
                                            <Search size={16} />
                                            検索モード
                                        </label>
                                        <div className={`search-mode-checkbox-wrapper ${enableSearchMode ? 'checked' : ''}`}>
                                            <label className="search-mode-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={enableSearchMode}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setEnableSearchMode(checked);
                                                        if (!checked) {
                                                            setSearchTimeRangeMode('none');
                                                            setSearchDateRestrict('w1');
                                                            setSearchDateFrom('');
                                                            setSearchDateTo('');
                                                        }
                                                    }}
                                                />
                                                <span>検索モードを有効化</span>
                                            </label>
                                        </div>
                                        {enableSearchMode && (
                                            <div className="search-time-range-container">
                                                <div className="search-time-range-mode">
                                                    <label className="search-time-range-radio">
                                                        <input
                                                            type="radio"
                                                            name="search-time-range-mode"
                                                            checked={searchTimeRangeMode === 'none'}
                                                            onChange={() => setSearchTimeRangeMode('none')}
                                                        />
                                                        <span>指定なし（上位順）</span>
                                                    </label>
                                                    <label className="search-time-range-radio">
                                                        <input
                                                            type="radio"
                                                            name="search-time-range-mode"
                                                            checked={searchTimeRangeMode === 'preset'}
                                                            onChange={() => setSearchTimeRangeMode('preset')}
                                                        />
                                                        <span>プリセット期間</span>
                                                    </label>
                                                    <label className="search-time-range-radio">
                                                        <input
                                                            type="radio"
                                                            name="search-time-range-mode"
                                                            checked={searchTimeRangeMode === 'absolute'}
                                                            onChange={() => setSearchTimeRangeMode('absolute')}
                                                        />
                                                        <span>日付指定</span>
                                                    </label>
                                                </div>

                                                {searchTimeRangeMode === 'preset' ? (
                                                    <div className="search-time-range-control">
                                                        <label className="search-time-range-label">期間</label>
                                                        <select
                                                            className="form-input"
                                                            value={searchDateRestrict}
                                                            onChange={(e) => setSearchDateRestrict(e.target.value)}
                                                        >
                                                            {SEARCH_DATE_RESTRICT_OPTIONS.map((option) => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ) : searchTimeRangeMode === 'absolute' ? (
                                                    <div className="search-time-range-date-row">
                                                        <div className="search-time-range-control">
                                                            <label className="search-time-range-label">開始日</label>
                                                            <input
                                                                type="date"
                                                                className="form-input"
                                                                value={searchDateFrom}
                                                                onChange={(e) => setSearchDateFrom(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="search-time-range-control">
                                                            <label className="search-time-range-label">終了日</label>
                                                            <input
                                                                type="date"
                                                                className="form-input"
                                                                value={searchDateTo}
                                                                onChange={(e) => setSearchDateTo(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="form-field form-field-full">
                                    <label className="form-label">
                                        <Link size={16} />
                                        URLリスト *
                                    </label>


                                    <textarea
                                        className="url-textarea"
                                        placeholder="https://example.com/article1&#10;https://example.com/article2&#10;https://example.com/article3"
                                        value={urlList}
                                        onChange={(e) => setUrlList(e.target.value)}
                                        rows={8}
                                    />
                                    {urlValidationSummary.hasAnyInput && urlValidationSummary.validCount === 0 && (
                                        <div className="url-validation-message error" role="status" aria-live="polite">
                                            <AlertTriangle size={16} aria-hidden />
                                            <span>
                                                有効なURLが見つかりません（`https://` または `http://` を含めてください）
                                                {urlValidationSummary.invalidUrls.length > 0 && (
                                                    <>
                                                        <br />
                                                        無効なURL:
                                                        {' '}
                                                        {urlValidationSummary.invalidUrls.slice(0, 5).join(', ')}
                                                        {urlValidationSummary.invalidUrls.length > 5
                                                            ? ` ほか${urlValidationSummary.invalidUrls.length - 5}件`
                                                            : ''}
                                                    </>
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    {urlValidationSummary.validCount > 0 && urlValidationSummary.invalidCount > 0 && (
                                        <div className="url-validation-message warning" role="status" aria-live="polite">
                                            <AlertTriangle size={16} aria-hidden />
                                            <span>
                                                無効なURLが {urlValidationSummary.invalidCount} 件あります。処理時に自動で除外されます。
                                                {' '}
                                                （例: {urlValidationSummary.invalidUrls.slice(0, 3).join(', ')}
                                                {urlValidationSummary.invalidUrls.length > 3
                                                    ? ` ほか${urlValidationSummary.invalidUrls.length - 3}件`
                                                    : ''}
                                                ）
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* パトロールデバイス: dropdown like other form inputs */}
                                <div className="form-field form-field-full">
                                    <label className="form-label">
                                        <Monitor size={16} />
                                        パトロールデバイス
                                    </label>
                                    <div className="dropdown-container device-mode-dropdown" ref={deviceModeDropdownRef}>
                                        <button
                                            type="button"
                                            className={`dropdown-button ${deviceMode ? 'selected' : ''}`}
                                            onClick={() => {
                                                setShowDeviceModeDropdown(!showDeviceModeDropdown);
                                                setShowGuidelineDropdown(false);
                                                setShowVersionDropdown(false);
                                                setShowASPDropdown(false);
                                                setShowOperationCompanyDropdown(false);
                                                setShowMediaDropdown(false);
                                            }}
                                            aria-expanded={showDeviceModeDropdown}
                                            aria-haspopup="listbox"
                                        >
                                            <span className="dropdown-button-value">
                                                {deviceMode === 'smart_phone' ? (
                                                    <><Smartphone size={18} aria-hidden /> モバイル</>
                                                ) : (
                                                    <><Monitor size={18} aria-hidden /> PC</>
                                                )}
                                            </span>
                                            <ChevronDown size={16} className="dropdown-chevron" aria-hidden />
                                        </button>
                                        {showDeviceModeDropdown && (
                                            <div className="dropdown-menu">
                                                <div className="dropdown-list">
                                                    <button
                                                        type="button"
                                                        className={`dropdown-item ${deviceMode === 'personal_computer' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setDeviceMode('personal_computer');
                                                            setShowDeviceModeDropdown(false);
                                                        }}
                                                    >
                                                        <Monitor size={18} aria-hidden />
                                                        <span>PC</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`dropdown-item ${deviceMode === 'smart_phone' ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setDeviceMode('smart_phone');
                                                            setShowDeviceModeDropdown(false);
                                                        }}
                                                    >
                                                        <Smartphone size={18} aria-hidden />
                                                        <span>モバイル</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>


                            <div className="url-input-footer">
                                <div className="url-count">
                                    {urlList ? parseUrlList(urlList).length : 0} 個の有効なURL
                                </div>
                                <button
                                    className="upload-button"
                                    onClick={handleUrlSubmit}
                                    disabled={!urlList.trim() || !selectedGuideline || selectedVersion === null || !selectedMedia || !selectedOperationCompany || !jobName.trim() || urlUploadLoading}
                                >
                                    {urlUploadLoading ? (
                                        <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                        <Copy size={16} />
                                    )}
                                    {urlUploadLoading ? 'アップロード中...' : 'URLを処理'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="upload-info">
                    <h4>URLリスト形式：</h4>
                    <ul>
                        <li>1行に1つのURLを入力</li>
                        <li>URLにはプロトコル（http://またはhttps://）を含める必要があります</li>
                        <li>無効なURLは自動的にフィルタリングされます</li>
                        <li>バッチあたり最大1000URL</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default UploadPage;
